import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { label } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import {
  Car, CarFront, Clock, Package, Cog, CalendarDays, PaintBucket, ShoppingCart,
  AlertTriangle, CheckSquare, Boxes, ClipboardList, Building2, Loader2, RefreshCw, Printer,
} from "lucide-react";
import { toast } from "sonner";

const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const short = (n) => { n = Number(n || 0); return n >= 1e6 ? (n / 1e6).toFixed(1).replace(".0", "") + "jt" : n >= 1e3 ? Math.round(n / 1e3) + "rb" : String(n); };
const todayStr = () => new Date().toLocaleDateString("en-CA");
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toLocaleDateString("en-CA"); };

export default function SAReport() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("today"); // today | yesterday | date | range
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [updatedAt, setUpdatedAt] = useState(null);
  const timer = useRef(null);

  const params = useCallback(() => {
    if (mode === "today") return { date: todayStr() };
    if (mode === "yesterday") return { date: yesterdayStr() };
    if (mode === "date") return { date: from };
    return { date_from: from, date_to: to };
  }, [mode, from, to]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get("/dashboard/sa-report", { params: params() }); setD(r.data); setUpdatedAt(new Date()); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
    if (!silent) setLoading(false);
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timer.current = setInterval(() => load(true), 180000);
    return () => clearInterval(timer.current);
  }, [load]);

  if (loading || !d) return <div className="flex min-h-[60vh] items-center justify-center text-gray-400"><Loader2 className="mr-2 animate-spin" size={18} /> Memuat SA Report…</div>;
  const k = d.kpi;
  const Nop = ({ id, v }) => id ? <button onClick={() => navigate(`/claims/${id}`)} className="font-semibold text-blue-700 underline-offset-2 hover:underline" data-testid={`nop-${id}`}>{v}</button> : <span>{v}</span>;
  const periodLabel = mode === "range" ? `${from} → ${to}` : (params().date || from);
  const upd = updatedAt ? updatedAt.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }).replace(".", ":") : "-";

  return (
    <div data-testid="sa-report" className="-m-3 min-h-screen bg-[#0c1c30] p-3 sm:-m-5 sm:p-5">
      {/* Header */}
      <div className="mb-3 flex flex-col gap-3 rounded-2xl bg-[#0f2340] p-4 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight sm:text-4xl">TITANIUM CORE</h1>
          <p className="mt-0.5 text-xs font-semibold tracking-widest text-sky-200/80 sm:text-sm">DASHBOARD HARIAN — SA REPORT</p>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm">
          <CalendarDays size={16} className="text-sky-300" /> <span className="font-semibold">{periodLabel}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="no-print mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-white/5 p-2">
        {[["today", "Hari ini"], ["yesterday", "Kemarin"], ["date", "Pilih tanggal"], ["range", "Range"]].map(([m, l]) => (
          <button key={m} data-testid={`sa-mode-${m}`} onClick={() => setMode(m)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${mode === m ? "bg-amber-400 text-[#0f2340]" : "bg-white/10 text-sky-100 hover:bg-white/20"}`}>{l}</button>
        ))}
        {mode === "date" && <Input data-testid="sa-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40 bg-white text-xs" />}
        {mode === "range" && <>
          <Input data-testid="sa-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36 bg-white text-xs" />
          <span className="text-white/50">→</span>
          <Input data-testid="sa-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36 bg-white text-xs" />
        </>}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-sky-200/70">Last updated: {upd}</span>
          <Button data-testid="sa-refresh" size="sm" variant="outline" className="h-8 border-white/20 bg-white/10 text-xs text-white hover:bg-white/20" onClick={() => load()}><RefreshCw size={13} className="mr-1" /> Refresh</Button>
          <Button data-testid="sa-print" size="sm" className="h-8 bg-amber-400 text-xs text-[#0f2340] hover:bg-amber-300" onClick={() => window.print()}><Printer size={13} className="mr-1" /> PDF</Button>
        </div>
      </div>

      {/* KPI */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={Car} color="#2563eb" label="OMZET PERIODE" value={rp(k.omzet_today)} big />
        <Kpi icon={CarFront} color="#16a34a" label="UNIT MASUK" value={k.unit_masuk} sub={`Est. ${rp(k.unit_masuk_est)}`} />
        <Kpi icon={CarFront} color="#f97316" label="UNIT KELUAR" value={k.unit_keluar} sub={`(${k.unit_keluar_invoiced} invoiced)`} />
        <Kpi icon={Clock} color="#7c3aed" label="AGING ≥3 HARI" value={k.aging} sub="Perlu follow up" subColor="#b45309" />
        <Kpi icon={Package} color="#d97706" label="PENDING PARTS" value={k.pending_parts} sub="Menunggu" subColor="#b45309" />
        <Kpi icon={Cog} color="#dc2626" label="BOTTLENECK" value={k.bottleneck} sub="Perlu aksi" subColor="#dc2626" />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="UNIT MASUK HARI INI" icon={Car} color="#1d4ed8">
          <Table head={["NO", "NOPOL", "UNIT", "ESTIMASI", "STATUS"]} rows={d.unit_masuk.map((u, i) => [i + 1, <Nop id={u.claim_id} v={u.nopol} />, u.unit, rp(u.estimasi), <Badge tone="green">{label(u.status)}</Badge>])} empty="Belum ada unit masuk." tid="unit-masuk" />
        </Section>
        <Section title="UNIT KELUAR HARI INI" icon={Car} color="#1d4ed8">
          <Table head={["NO", "NOPOL", "UNIT", "INVOICE", "STATUS"]} rows={d.unit_keluar.map((u, i) => [i + 1, <Nop id={u.claim_id} v={u.nopol} />, u.unit, rp(u.invoice), <Badge tone={u.status === "CLOSED" ? "green" : "amber"}>{u.status === "CLOSED" ? "Closed ✓" : "Waiting ⏳"}</Badge>])} empty="Belum ada unit keluar." tid="unit-keluar" />
        </Section>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="WO / SPK TERBIT" icon={ClipboardList} color="#ea580c">
          <Table head={["NO", "NOPOL", "NILAI WO", "PROGRESS"]} rows={d.wo_terbit.map((w, i) => [i + 1, <Nop id={w.claim_id} v={w.nopol} />, rp(w.nilai_wo), <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">{w.progress}%</span>])} empty="Belum ada WO terbit." tid="wo-terbit" />
        </Section>
        <Section title="AGING UNIT (≥3 HARI)" icon={Clock} color="#7c3aed">
          <Table head={["NO", "NOPOL", "SEJAK", "KETERANGAN"]} rows={d.aging.map((a, i) => [i + 1, <Nop id={a.claim_id} v={a.nopol} />, <span className="font-semibold text-red-500">{a.sejak}</span>, a.keterangan])} empty="Tidak ada unit aging." tid="aging" />
        </Section>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="PENDING PARTS" icon={Boxes} color="#d97706">
          <Table head={["NO", "PARTS", "UNIT", "KETERANGAN"]} rows={d.pending_parts.map((p, i) => [i + 1, p.parts, <Nop id={p.claim_id} v={p.unit} />, p.keterangan])} empty="Tidak ada parts pending." tid="pending-parts" />
        </Section>
        <Section title="BOTTLENECK PRODUKSI" icon={AlertTriangle} color="#dc2626">
          <div className="space-y-2 p-3">
            <BotRow icon={PaintBucket} color="#3b82f6" label="Cat kurang stok" value={d.bottleneck.cat_kurang_stok} />
            <BotRow icon={Cog} color="#0ea5e9" label="Mixing belum matching" value={d.bottleneck.mixing_belum_matching} />
            <BotRow icon={ShoppingCart} color="#f59e0b" label="Parts belum datang" value={d.bottleneck.parts_belum_datang} />
          </div>
        </Section>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="PROGRESS PRODUKSI (KANBAN)" icon={Building2} color="#0f2340">
          <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4">
            <KanCol title="SIAP KELUAR BESOK" tone="bg-amber-100 text-amber-800" items={d.kanban.READY_TO_DELIVERY} nav={navigate} />
            <KanCol title="SIAP CAT" tone="bg-teal-100 text-teal-800" items={d.kanban.READY_FOR_PAINT} nav={navigate} />
            <KanCol title="PROSES CAT" tone="bg-orange-100 text-orange-800" items={d.kanban.PAINT} nav={navigate} />
            <KanCol title="POLES/FINISH" tone="bg-blue-100 text-blue-800" items={d.kanban.FINISH} nav={navigate} />
          </div>
        </Section>
        <Section title="OMZET MINGGU INI" icon={Building2} color="#0f2340">
          <div className="p-3" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.omzet_week} margin={{ top: 10, right: 6, left: -10, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={short} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {d.omzet_week.map((e, i) => <Cell key={i} fill={i === 6 ? "#f59e0b" : "#2563eb"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="10 UNIT DENGAN PROGRESS AKTIF" icon={CheckSquare} color="#0f2340">
          <Table head={["NOPOL", "MODEL", "STATUS", "SEJAK", "PIC"]} rows={d.active_progress.map((a) => [<Nop id={a.claim_id} v={a.nopol} />, a.model, <Badge tone="amber">{label(a.status)}</Badge>, a.sejak, a.pic])} empty="Tidak ada unit aktif." tid="active" />
        </Section>
        <div data-testid="alerts" className="overflow-hidden rounded-2xl bg-[#0f2340] text-white shadow-lg">
          <div className="flex items-center gap-2 px-4 py-3 text-sm font-bold tracking-wide"><AlertTriangle size={16} className="text-amber-300" /> ALERT HARI INI</div>
          <div className="space-y-2 px-4 pb-4">
            {d.alerts.length === 0 && <p className="text-sm text-sky-200/70">Tidak ada alert. Semua terkendali ✔</p>}
            {d.alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm">
                <span className={`h-4 w-4 shrink-0 rounded ${a.level === "warn" ? "bg-amber-400" : "bg-sky-400"}`} />
                <span className="text-gray-800">{a.text}</span>
              </div>
            ))}
            {d.alerts.length > 0 && <div className="mt-1 rounded-lg bg-amber-100/90 px-3 py-2 text-xs font-semibold text-amber-900">Follow up segera untuk hindari keterlambatan delivery.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, sub, subColor, big }) {
  return (
    <div className="rounded-2xl bg-white p-3 text-center shadow-sm">
      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full" style={{ background: color + "1a" }}><Icon size={18} style={{ color }} /></div>
      <div className="mt-1.5 text-[9px] font-bold uppercase tracking-wider text-gray-400 sm:text-[10px]">{label}</div>
      <div className={`font-black text-[#0f2340] ${big ? "text-lg sm:text-xl" : "text-2xl"}`}>{value}</div>
      {sub && <div className="text-[10px] font-medium" style={{ color: subColor || "#6b7280" }}>{sub}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, color, children }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
      <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold tracking-wide text-white" style={{ background: color }}><Icon size={16} /> {title}</div>
      {children}
    </div>
  );
}

function Table({ head, rows, empty, tid }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid={tid} className="w-full text-left text-sm">
        <thead><tr className="border-b border-gray-100 text-[10px] uppercase tracking-wide text-gray-400">{head.map((h) => <th key={h} className="px-3 py-1.5 font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i} className="border-b border-gray-50 last:border-0">{r.map((c, j) => <td key={j} className="px-3 py-1.5 text-gray-700">{c}</td>)}</tr>)}
          {rows.length === 0 && <tr><td colSpan={head.length} className="px-3 py-6 text-center text-xs text-gray-400">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Badge({ tone, children }) {
  const m = { green: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700" }[tone] || "bg-gray-100 text-gray-600";
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${m}`}>{children}</span>;
}

function BotRow({ icon: Icon, color, label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2">
      <div className="flex items-center gap-2"><span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: color + "1a" }}><Icon size={16} style={{ color }} /></span><span className="text-sm font-medium text-gray-700">{label}</span></div>
      <span className="text-lg font-black text-[#0f2340]">{value} <span className="text-xs font-medium text-gray-400">unit</span></span>
    </div>
  );
}

function KanCol({ title, tone, items, nav }) {
  return (
    <div className="rounded-xl bg-gray-50 p-2">
      <div className={`rounded-md px-2 py-1 text-center text-[10px] font-bold uppercase tracking-wide ${tone}`}>{title}</div>
      <div className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <button key={i} onClick={() => it.claim_id && nav(`/claims/${it.claim_id}`)} className="block w-full rounded-lg border border-gray-100 bg-white px-2 py-1.5 text-left shadow-sm hover:border-[#3B82F6]">
            <div className="text-[13px] font-bold text-[#0f2340]">{it.nopol}</div>
            <div className="text-[10px] text-gray-400">{it.note}</div>
          </button>
        ))}
        {items.length === 0 && <div className="py-3 text-center text-[10px] text-gray-300">—</div>}
      </div>
    </div>
  );
}
